// Sprint F · Feature 2 — per-tab document section.
//
// Renders under each ProjectDetail tab (land/grid/environment/planning/
// financials). Reads from the project's `documents` JSONB array, filtered
// to the current tab. Upload posts multipart to the backend; metadata
// goes into JSONB, raw bytes go to /var/seidr/documents (docker volume).
import React, { useRef, useState } from 'react';
import { useCurrentUser } from '../contexts/TeamContext';
import '../styles/DocumentSection.css';

const API = (import.meta.env?.VITE_API_URL || '') + '/api/v1/projects';

const fmtBytes = (n) => {
  if (n == null) return '';
  if (n < 1024)        return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const fmtAgo = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days < 1)  return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
};

const fileIcon = (mime) => {
  if (!mime) return '📄';
  if (mime === 'application/pdf')         return '📕';
  if (mime.includes('spreadsheet') || mime.includes('excel')) return '📊';
  if (mime.includes('word') || mime.includes('document'))     return '📝';
  if (mime.startsWith('image/'))          return '🖼';
  if (mime.startsWith('text/'))           return '📃';
  return '📎';
};

export function DocumentSection({ projectId, tabKey, documents, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const currentUser = useCurrentUser();

  const tabDocs = (documents || []).filter(d =>
    d.tab_key === tabKey && !d.deleted_at
  );

  const handleUpload = async (file) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tab_key', tabKey);
      fd.append('user_id',   currentUser?.id   || 'anon');
      fd.append('user_name', currentUser?.name || 'Anonymous');
      const r = await fetch(`${API}/${projectId}/documents/upload`, {
        method: 'POST', body: fd,
      });
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(`${r.status}: ${txt.slice(0, 200)}`);
      }
      onChange?.();
    } catch (e) {
      setError(e.message);
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDelete = async (doc) => {
    if (!confirm(`Delete ${doc.filename}? (Soft-delete — file kept on disk for audit.)`)) return;
    try {
      const qs = new URLSearchParams({ user_name: currentUser?.name || 'Anonymous' });
      const r = await fetch(`${API}/${projectId}/documents/${doc.id}?${qs}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`${r.status}`);
      onChange?.();
    } catch (e) {
      setError(`Delete failed: ${e.message}`);
    }
  };

  return (
    <div className="doc-section">
      <div className="doc-section-header">
        <span className="doc-section-title">Documents · {tabKey}</span>
        <button className="doc-upload-btn" onClick={() => inputRef.current?.click()}
                disabled={uploading || !currentUser}
                title={!currentUser ? 'Sign in to upload' : ''}>
          {uploading ? 'Uploading…' : '+ Upload'}
        </button>
        <input ref={inputRef} type="file" style={{ display: 'none' }}
               onChange={(e) => handleUpload(e.target.files?.[0])} />
      </div>

      {error && <div className="doc-error">{error}</div>}

      {tabDocs.length === 0 ? (
        <div className="doc-empty">No documents yet for this section.</div>
      ) : (
        <div className="doc-list">
          {tabDocs.map(d => (
            <div key={d.id} className="doc-item">
              <span className="doc-icon">{fileIcon(d.mime_type)}</span>
              <div className="doc-meta">
                <div className="doc-filename">{d.filename}</div>
                <div className="doc-sub">
                  {fmtBytes(d.file_size_bytes)}
                  {d.uploaded_by_name && ` · uploaded by ${d.uploaded_by_name}`}
                  {' · '}{fmtAgo(d.uploaded_at)}
                </div>
              </div>
              <div className="doc-actions">
                <a className="doc-action-link"
                   href={`${API}/${projectId}/documents/${d.id}/raw`}
                   target="_blank" rel="noopener noreferrer">view</a>
                <a className="doc-action-link"
                   href={`${API}/${projectId}/documents/${d.id}/raw`}
                   download={d.filename}>download</a>
                <button className="doc-action-link doc-action-delete"
                        onClick={() => handleDelete(d)}>delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
